import {
  ApiClient,
  ApiClientContext,
  ApiClientOptions,
  RouteOptions,
  defaultAxiosConfig,
} from '@qualifio/api-client';
import { AxiosInstance } from 'axios';
import { QualifioLogger } from '@qualifio/logger';
import { CampaignChannel } from './types/campaign-channel';

export interface EngageManagerApiClientParams {
  // If not present, the manager api might return a 404 when requesting an entity or an empty array when requesting a list
  clientId?: number;
}

export interface EngageManagerApiClientOptions extends ApiClientOptions {
  token?: string;
}

export interface ListCampaignsParams {
  plannings?: PlanningAllowed[];
  campaignIds?: string[];
  withChannels?: boolean;
}

export interface GetCampaignParams {
  withChannels?: boolean;
}

export enum PlanningAllowed {
  ALL = 'all',
  ACTIVE = 'ACTIVE',
  UPCOMING = 'UPCOMING',
  EXPIRED = 'EXPIRED',
}

export class EngageManagerApiClient extends ApiClient {
  static serviceName = 'engage-manager-api';

  constructor(
    http?: AxiosInstance,
    context?: ApiClientContext,
    options?: EngageManagerApiClientOptions,
  ) {
    super('', http, context, options);

    if (options?.token && http) {
      http.interceptors.request.use(config => {
        config.headers['x-api-token'] = options?.token;
        return config;
      });
    }
  }

  /**
   * To get integrationTags, channelId needs to be the productionId (string) and integrationTags must be true.
   * @param channelId
   * @param integrationTags
   * @param options
   */
  async getChannel<I extends string | number>(
    channelId: I,
    integrationTags = false,
    options?: RouteOptions<EngageManagerApiClientParams>,
  ): Promise<CampaignChannel> {
    const { data } = await this.http.get(
      `/channels/${channelId}`,
      defaultAxiosConfig({ ...options, params: { integrationTags, ...options?.params } }),
    );
    return data;
  }

  async getChannels(
    productionIds: string[],
    logger: QualifioLogger,
  ): Promise<{
    [channelId: string]: CampaignChannel | null;
  }> {
    const channels: { [productionId: string]: CampaignChannel } = {};
    for (const productionId of productionIds) {
      try {
        channels[productionId] = await this.getChannel(productionId, true, {
          cache: { ttl: 300_000 },
        });
      } catch (error) {
        logger.warn({ error, metadata: { productionId } }, 'Failed to find channel');
      }
    }
    return channels;
  }
}
